import { useEffect, useMemo, useState } from "react"
import { useRoutesHooks } from "../../../../../features/routes"
import { DetailWrapper, SpinnerDiv } from "../../../../../features/ui"
import { Customer, SaleOrder } from "../../../state/types"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { useSaleLoaders } from "../../../loaders/saleLoaders"
import { defined } from "../../../../../utils/listUtils"
import { AdjustmentNote, Check, CreditPayment, useAdjustmentNoteLoaders, useCheckLoaders, useCreditPaymentLoaders } from "../../../../administration"
import { SaleDetail } from "./saleDetail"
import { useFiscalDocumentLoaders, FiscalDocument, FiscalDocumentStatus, useFiscalDocumentAutoRefresh } from "../../../../fiscal-documents"
import { Delivery, DeliveryOrder, useDeliveryOrderLoaders, useDeliveryLoaders } from "../../../../deliveries"
import { useServer } from "../../../../../services/server"

export const Loader = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const sale = dataType! as SaleOrder

    const [ready, setReady] = useState(false)
    const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
    const [deliveries, setDeliveries] = useState<Delivery[]>([])
    const [customer, setCustomer] = useState<Customer>()
    const [checks, setChecks] = useState<Check[]>([])
    const [creditPayments, setCreditPayments] = useState<CreditPayment[]>([])
    const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([])
    const [adjustmentNotes, setAdjustmentNotes] = useState<AdjustmentNote[]>([])

    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const customerLoaders = useCustomerLoaders()
    const checkLoaders = useCheckLoaders()
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()
    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()
    const saleLoaders = useSaleLoaders()

    const { refreshFiscalDocumentStatus } = useServer()

    const loadDeliveries = async (optimized = true) => {
        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadOperationDeliveryOrders(sale.id, optimized)
        setDeliveryOrders(loadedDeliveryOrders)
        const { loadOperationDeliveries } = deliveryLoaders()
        const loadedDeliveries = await loadOperationDeliveries(sale.id, optimized)
        setDeliveries(loadedDeliveries)
    }

    // El estado de facturación también vive denormalizado en la operación (invoiceStatus). Al refrescar
    // el documento hay que releer la venta para que la UI (botón Facturar, editabilidad, lista) no quede vieja.
    const reloadFiscalDocuments = async () => {
        const { loadSale } = saleLoaders()
        await loadSale(sale.id, true)
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        setFiscalDocuments(await loadOperationFiscalDocuments(sale.id, false))
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        setAdjustmentNotes(await loadOperationAdjustmentNotes(sale.id, false))
    }

    // Annulling or cancelling pending items can also generate a credit note
    const reloadDeliveries = async () => {
        await loadDeliveries(false)
        await reloadFiscalDocuments()
    }

    // Autorefresco: mientras haya comprobantes pendientes de autorizar, consultamos Facturante y
    // releemos. Se detiene solo al llegar a estado terminal o al agotar la ventana (~30s), momento
    // en que la UI vuelve a ofrecer el refresh manual.
    const pendingFiscalDocumentIds = useMemo(
        () => fiscalDocuments
            .filter(document => document.status === FiscalDocumentStatus.TO_AUTHORIZE)
            .map(document => document.id),
        [fiscalDocuments]
    )

    const refreshPendingFiscalDocuments = async (pendingIds: string[]) => {
        await Promise.all(pendingIds.map(id => refreshFiscalDocumentStatus(id)))
        await reloadFiscalDocuments()
    }

    const { isExhausted: isAutoRefreshExhausted } = useFiscalDocumentAutoRefresh(
        pendingFiscalDocumentIds,
        refreshPendingFiscalDocuments
    )

    const load = async () => {
        await loadDeliveries()
        const { loadCustomer } = customerLoaders()
        const loadedCustomer = await loadCustomer(sale.customerId)
        setCustomer(loadedCustomer)
        const { loadChecks } = checkLoaders()
        const destinationIds = defined(sale.paymentItems.map(paymentItem => paymentItem.destinationId))
        const checks = await loadChecks(destinationIds)
        setChecks(checks)
        const { loadCustomerCreditPayments } = creditPaymentLoaders()
        const loadedCreditPayments = await loadCustomerCreditPayments(sale.customerId)
        const operationCreditPayments = loadedCreditPayments.filter(creditPayment => creditPayment.operationItems.some(operationItem => operationItem.operationId === sale.id))
        setCreditPayments(operationCreditPayments)
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        const loadedFiscalDocuments = await loadOperationFiscalDocuments(sale.id)
        setFiscalDocuments(loadedFiscalDocuments)
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        const loadedAdjustmentNotes = await loadOperationAdjustmentNotes(sale.id)
        setAdjustmentNotes(loadedAdjustmentNotes)
    }
    
    useEffect(() => {
        load().then(() => setReady(true))
    }, [])

    return ready ? (
        <SaleDetail
            sale={sale}
            deliveryOrders={deliveryOrders}
            deliveries={deliveries}
            checks={checks}
            customer={customer}
            creditPayments={creditPayments}
            fiscalDocuments={fiscalDocuments}
            adjustmentNotes={adjustmentNotes}
            onRefreshDeliveries={reloadDeliveries}
            onRefreshFiscalDocuments={reloadFiscalDocuments}
            isAutoRefreshExhausted={isAutoRefreshExhausted}
        />
    ) : <SpinnerDiv />
}

export const SaleDetailLoader = () => (
    <DetailWrapper>
        <Loader />
    </DetailWrapper>
)
